import React from "react";

// Shimmer placeholders shown while a page's first request is in flight.
// Built on the .ss-skeleton shimmer in App.css so every page loads the same way.

export const Skeleton = ({ width = "100%", height = 14, radius = 8, className = "", style }) => (
  <div
    className={`ss-skeleton ${className}`}
    style={{ width, height, borderRadius: radius, ...style }}
    aria-hidden="true"
  />
);

export const SkeletonText = ({ lines = 3, className = "" }) => (
  <div className={className}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} height={12} width={i === lines - 1 ? "60%" : "100%"} className="mb-2" />
    ))}
  </div>
);

export const HeaderSkeleton = ({ withActions = false }) => (
  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
    <div style={{ flex: "1 1 240px" }}>
      <Skeleton width={220} height={26} className="mb-2" />
      <Skeleton width={320} height={12} style={{ maxWidth: "100%" }} />
    </div>
    {withActions && (
      <div className="d-flex gap-2">
        <Skeleton width={110} height={36} radius={10} />
        <Skeleton width={110} height={36} radius={10} />
      </div>
    )}
  </div>
);

export const TabsSkeleton = ({ count = 4 }) => (
  <div className="d-flex gap-2 mb-3 overflow-hidden">
    {Array.from({ length: count }).map((_, i) => (
      <Skeleton key={i} width={100} height={32} radius={10} style={{ flex: "0 0 auto" }} />
    ))}
  </div>
);

export const KpiSkeleton = ({ count = 4 }) => (
  <div className="row g-3 mb-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="col-6 col-lg-3">
        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 14 }}>
          <div className="card-body d-flex align-items-center gap-3">
            <Skeleton width={44} height={44} radius={12} style={{ flex: "0 0 auto" }} />
            <div className="flex-grow-1">
              <Skeleton width="55%" height={10} className="mb-2" />
              <Skeleton width="80%" height={20} />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const TableSkeleton = ({ rows = 8, cols = 5, withToolbar = true }) => (
  <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
    <div className="card-body">
      {withToolbar && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Skeleton width={240} height={36} radius={10} style={{ maxWidth: "100%" }} />
          <Skeleton width={140} height={36} radius={10} />
          <div className="ms-auto">
            <Skeleton width={100} height={36} radius={10} />
          </div>
        </div>
      )}
      <div className="d-flex gap-3 pb-2 mb-2 border-bottom">
        {Array.from({ length: cols }).map((_, c) => (
          <Skeleton key={c} height={12} width={`${100 / cols}%`} />
        ))}
      </div>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="d-flex gap-3 py-2">
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} height={14} width={`${100 / cols}%`} style={{ opacity: c === 0 ? 1 : 0.8 }} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const CardListSkeleton = ({ count = 4 }) => (
  <div className="d-grid gap-2">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
        <div className="card-body">
          <div className="d-flex justify-content-between mb-2">
            <Skeleton width="45%" height={16} />
            <Skeleton width={70} height={20} radius={999} />
          </div>
          <Skeleton width="70%" height={12} className="mb-2" />
          <Skeleton width="35%" height={12} />
        </div>
      </div>
    ))}
  </div>
);

export const CardGridSkeleton = ({ count = 6, imageHeight = 140, colClass = "col-12 col-sm-6 col-lg-4" }) => (
  <div className="row g-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={colClass}>
        <div className="card border-0 shadow-sm h-100 overflow-hidden" style={{ borderRadius: 14 }}>
          {imageHeight > 0 && <Skeleton height={imageHeight} radius={0} />}
          <div className="card-body">
            <Skeleton width="70%" height={16} className="mb-2" />
            <SkeletonText lines={2} />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const ChartSkeleton = ({ height = 260 }) => (
  <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
    <div className="card-body">
      <Skeleton width={160} height={16} className="mb-3" />
      <div className="d-flex align-items-end gap-2" style={{ height }}>
        {[55, 80, 40, 95, 65, 75, 50, 85, 60, 70].map((h, i) => (
          <Skeleton key={i} height={`${h}%`} radius={6} style={{ flex: 1 }} />
        ))}
      </div>
    </div>
  </div>
);

export const FormSkeleton = ({ fields = 5 }) => (
  <div>
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="mb-3">
        <Skeleton width={120} height={12} className="mb-2" />
        <Skeleton height={40} radius={10} />
      </div>
    ))}
    <Skeleton width={140} height={40} radius={10} />
  </div>
);

// Ready-made page layouts. `variant` picks what the page body looks like.
export const PageSkeleton = ({ variant = "table", tabs = 0, kpis = 0, rows = 8, cols = 5, cards = 6 }) => (
  <div className="container py-3" role="status" aria-live="polite" aria-label="Loading">
    <HeaderSkeleton withActions={variant === "table"} />
    {tabs > 0 && <TabsSkeleton count={tabs} />}
    {kpis > 0 && <KpiSkeleton count={kpis} />}
    {variant === "table" && <TableSkeleton rows={rows} cols={cols} />}
    {variant === "list" && <CardListSkeleton count={cards} />}
    {variant === "grid" && <CardGridSkeleton count={cards} />}
    {variant === "summary" && (
      <div className="row g-3">
        <div className="col-12 col-lg-8"><ChartSkeleton /></div>
        <div className="col-12 col-lg-4"><ChartSkeleton /></div>
        <div className="col-12"><TableSkeleton rows={5} cols={cols} withToolbar={false} /></div>
      </div>
    )}
    {variant === "media" && (
      <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
        <div className="card-body">
          <Skeleton width={90} height={10} className="mb-2" />
          <Skeleton width="45%" height={22} className="mb-3" />
          <Skeleton height={0} radius={12} style={{ paddingTop: "56.25%" }} className="mb-3" />
          <SkeletonText lines={3} />
        </div>
      </div>
    )}
    {variant === "form" && (
      <div className="card border-0 shadow-sm" style={{ borderRadius: 14, maxWidth: 640 }}>
        <div className="card-body"><FormSkeleton /></div>
      </div>
    )}
    <span className="visually-hidden">Loading…</span>
  </div>
);

export default Skeleton;
